import _ from 'underscore';
import React, { useCallback, useContext, useMemo, useState, useEffect, useRef } from 'react';
import {
  TopChangesFilterOption,
  getComparisonDateRange,
} from '@bingads-webui-campaign/overview-cards-common';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import cacheManager from '@bingads-webui-campaign-legacy/cache-manager/index';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { OverviewTopChanges } from '@bingads-webui-campaign-react/overview-top-changes';
import * as metricFormatter from '@bingads-webui-campaign-legacy/performance-metric-formatter/index';
import { getRoutePathById, allSiteMap } from '@bingads-webui-react/with-site-map';
import { useHeaderStateContext } from '@bingads-webui-campaign-react/overview-card';
import { getTopChangesAllMetrics } from '@bingads-webui-campaign/fluent-page-data';

import { useTopChangesData } from './use-top-changes-data';
import { useFluctuationPopup } from './use-fluctuation-popup';

const entityName = {
  customer: 'Account',
  account: 'Campaign',
  campaign: 'AdGroup',
};

type TopChangesCardProps = {
  i18n: {
    getString: (key: string, params?: any) => string;
  };
  dateRange: { start: Date; end: Date; };
  legacyI18n: any;
  filter: any,
  jsdata: object;
  dateUtil: any;
  cardTypeId: string;
  constructOptions: Function;
  navigationLevel: string;
  perfMarkerUtils: {
    willFetchData: Function;
    willRender: Function;
    done: Function;
  };
  getCardsConfig: Function;
  preferenceFromServer: string;
  patchPreference: Function;
  campaignType: number | string;
};

export const TopChangesCard: React.FC<TopChangesCardProps> = ({
  i18n,
  legacyI18n,
  dateRange,
  filter,
  jsdata,
  dateUtil,
  cardTypeId,
  constructOptions,
  navigationLevel,
  perfMarkerUtils,
  getCardsConfig,
  preferenceFromServer,
  patchPreference,
  campaignType,
}) => {
  const permissions = useContext(PermissionsContext);
  const { scope } = useContext(ScopeContext) as any;
  const {
    aid, cid, uid, campaignId,
  } = scope?.values;
  const comparisonDateRange = getComparisonDateRange(dateRange);
  const headerState = useHeaderStateContext();
  const selectedMenuItem = headerState?.selectedItemValue || TopChangesFilterOption.AllChanges;
  const [dismissLastFluctuation, setDismissLastFluctuation] = useState(false);
  const fetchSingleTile = useRef(false);

  const [selectedMetric, setSelectedMetric] = useState(preferenceFromServer);
  const onMetricSelect = useCallback((selection) => {
    patchPreference({ metric: selection });
    setSelectedMetric(selection);
    fetchSingleTile.current = true;
  }, [patchPreference]);

  useEffect(() => {
    fetchSingleTile.current = true;
  }, [selectedMenuItem]);

  useEffect(() => {
    fetchSingleTile.current = false;
  }, [dateRange]);

  const onFooterButtonClick = useCallback(() => {
    if (navigationLevel === 'account') {
      scope.save(getRoutePathById(allSiteMap, 'campaigns'));
    } else if (navigationLevel === 'customer') {
      scope.save(getRoutePathById(allSiteMap, 'performance'));
    } else if (navigationLevel === 'campaign') {
      scope.save(getRoutePathById(allSiteMap, 'adgroups'));
    }
  }, [navigationLevel, scope]);

  const footerButtonConfigs = useMemo(() => {
    switch (navigationLevel) {
      case 'account':
        return [
          {
            text: i18n.getString(_TL_('View all campaigns')),
            onClick: onFooterButtonClick,
          },
        ];
      case 'customer':
        return [
          {
            text: i18n.getString(_TL_('View all accounts')),
            onClick: onFooterButtonClick,
          },
        ];

      case 'campaign':
        return [
          {
            text: i18n.getString(_TL_('View all ad groups')),
            onClick: onFooterButtonClick,
          },
        ];
      default:
        return [];
    }
  }, [navigationLevel, i18n, onFooterButtonClick]);

  const getHref = useCallback((entityId, campaignType) => {
    if (navigationLevel === 'account' && campaignType === 'Smart') {
      return `/campaign/smart?cid=${cid}&uid=${uid}&aid=${aid}#/campaign/${entityId}`;
    }

    let route = getRoutePathById(allSiteMap, 'overview');
    let search = '';
    switch (navigationLevel) {
      case 'account':
        search = `cid=${cid}&uid=${uid}&aid=${aid}&campaignId=${entityId}`;
        break;
      case 'customer':
        search = `cid=${cid}&uid=${uid}&aid=${entityId}`;
        break;
      case 'campaign':
        route = getRoutePathById(allSiteMap, 'keywords');
        search = `cid=${cid}&uid=${uid}&aid=${aid}&campaignId=${campaignId}&adGroupId=${entityId}`;
        break;
      default:
    }

    return `${route}?${search}`;
  }, [navigationLevel, cid, uid, aid, campaignId]);


  const { isLoading, data, error } = useTopChangesData({
    dateRange,
    filter,
    comparisonDateRange,
    selectedMenuItem,
    metric: selectedMetric,
    jsdata,
    dateUtil,
    permissions,
    cardTypeId,
    constructOptions,
    dismissLastFluctuation,
    cacheManager,
    fetchSingleTile: fetchSingleTile.current,
    perfMarkerUtils,
    i18n,
    getCardsConfig,
    campaignType,
  });

  useEffect(() => {
    if (!isLoading) {
      perfMarkerUtils?.done();
    }
  }, [perfMarkerUtils, isLoading]);

  const allMetrics = useMemo(() => getTopChangesAllMetrics(i18n), [i18n]);

  const renderFluctuationPopup = useFluctuationPopup({
    legacyI18n, dateRange, setDismissLastFluctuation, navigationLevel,
  });

  return (<OverviewTopChanges
    isLoading={isLoading}
    selectedMetric={selectedMetric}
    onMetricSelect={onMetricSelect}
    metrics={allMetrics}
    data={data || []}
    i18n={i18n}
    metricFormatter={metricFormatter}
    footerButtonConfigs={footerButtonConfigs}
    getHref={getHref}
    entityType={entityName[navigationLevel] || ''}
    currentDateRange={dateRange}
    comparisonDateRange={comparisonDateRange}
    renderFluctuationPopup={renderFluctuationPopup}
  />);
};
